import { Link } from 'react-router-dom';
import ComplianceDisclaimer from './ComplianceDisclaimer';
import { CITIES, FUNDING_TYPES } from '../../config/cities';

declare global {
  interface Window { gtag?: (...args: unknown[]) => void; }
}

function trackForSale(source: string) {
  if (window.gtag) window.gtag('event', 'for_sale_click', { source });
}

export default function Footer() {
  const currentYear = new Date().getFullYear();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <footer className="bg-white border-t border-gray-200 mt-16 w-full overflow-hidden">
      {/* FOR SALE Acquisition Banner */}
      <div className="bg-[#0f0f0f] w-full">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex flex-col md:flex-row items-center justify-between gap-5">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="bg-[#DC2626] text-white text-xs font-black px-2.5 py-1 rounded-full uppercase tracking-widest">For Sale</span>
              <span className="text-white font-black text-lg">OWN THIS WEBSITE</span>
            </div>
            <p className="text-gray-400 text-sm max-w-md">
              BusinessCapitalGuide.net is available for acquisition. A turnkey SEO &amp; lead gen asset — fully built, monetization-ready, and admin-managed.
            </p>
          </div>
          <Link
            to="/for-sale"
            onClick={() => trackForSale('footer_section')}
            className="flex-shrink-0 flex items-center gap-2 bg-[#DC2626] hover:bg-red-700 text-white font-bold px-6 py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-store-line"></i>
            Available for Acquisition
          </Link>
        </div>
      </div>

      {/* Compliance Disclaimer Section */}
      <ComplianceDisclaimer variant="footer" />
      
      {/* Footer Links & Copyright */}
      <div className="w-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8 mb-8">
          {/* About */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">About</h3>
            <p className="text-sm text-gray-600 leading-relaxed">
              Educational platform providing guidance on business funding options. 
              We help businesses understand their financing possibilities.
            </p>
          </div>

          {/* Business Funding by City */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Business Funding by City</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              {CITIES.map((city) => (
                <li key={city.slug}>
                  <Link 
                    to={`/cities/${city.slug}`}
                    onClick={scrollToTop}
                    className="hover:text-red-600 transition-colors cursor-pointer inline-block"
                  >
                    {city.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Funding Types */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Funding Types</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              {FUNDING_TYPES.map((fundingType) => (
                <li key={fundingType.slug}>
                  <Link 
                    to={`/funding/${fundingType.slug}`}
                    onClick={scrollToTop}
                    className="hover:text-red-600 transition-colors inline-block"
                  >
                    {fundingType.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="font-semibold text-gray-900 mb-3 sm:mb-4 text-base sm:text-lg">Legal</h3>
            <ul className="space-y-2 text-sm text-gray-600">
              <li>
                <Link 
                  to="/privacy-policy" 
                  onClick={scrollToTop}
                  className="hover:text-red-600 transition-colors cursor-pointer inline-block"
                >
                  Privacy Policy
                </Link>
              </li>
              <li>
                <Link 
                  to="/terms-of-service" 
                  onClick={scrollToTop}
                  className="hover:text-red-600 transition-colors cursor-pointer inline-block"
                >
                  Terms of Service
                </Link>
              </li>
              <li>
                <Link 
                  to="/affiliate-disclosure" 
                  onClick={scrollToTop}
                  className="hover:text-red-600 transition-colors cursor-pointer inline-block"
                >
                  Affiliate Disclosure
                </Link>
              </li>
            </ul>
          </div>
        </div>

        {/* Copyright & Attribution */}
        <div className="border-t border-gray-200 pt-6 text-center">
          <p className="text-sm text-gray-600 mb-2">
            © {currentYear} Business Capital Guide. All rights reserved.
          </p>
          <p className="text-xs text-gray-500 mb-4 px-4">
            <strong>Affiliate Disclosure:</strong> We may receive compensation if funding is secured through our partner network. 
            This does not affect the terms or availability of any funding products.
          </p>
          <div className="mt-4">
            <a 
              href="https://readdy.ai/?ref=logo" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
            >
              Powered by Readdy
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
}
